// Operator Analytics — v1.1 §3.4, build-instruction §3.4. G6.
//
// Backend: E5, `GET /api/operators/{op_id}/analytics`
// (`backend/routers/ops.py:194`, lane BE-ANALYTICS). Access mirrors
// `GET /operators/{op_id}`: Founder, or the operator reading their own
// analytics. A non-founder here always sees their own numbers — there is no
// selector for them, because the server would 403 anything else.
//
// FIX-ANALYTICS-SCHEMA (docs/LANE_MANIFEST.md) found two live schemas writing
// `operator_decision_log`; the endpoint now reads both and surfaces rows with
// no FOLLOWED/OVERRIDDEN/IGNORED marker as an honest "uncategorized" bucket
// rather than guessing. This surface renders that bucket rather than hiding
// it, and does not compute a single operator score — §3.5's "no speculative
// performance judgments" doctrine, raw counts and ratios only.
import React, { useEffect, useMemo, useState } from "react";

import { useBrainResource } from "../../../components/brain/lib/hooks";
import EmptyState from "../../components/EmptyState";
import PageHead, { SectionTitle } from "../../components/PageHead";
import { useShell } from "../../ShellContext";

const STATES = {
  loading: {
    title: "Loading operator analytics…",
    detail: "Reading the decision log for this operator.",
  },
  disabled: {
    title: "Operator analytics is not available",
    detail: "The endpoint returned 404 — it is not mounted in this environment.",
  },
  denied: {
    title: "Access refused",
    detail:
      "The server refused this request. An operator may read their own analytics; anyone else's requires Founder access.",
  },
  notfound: {
    title: "Operator not found",
    detail: "No operator record exists for this id.",
  },
  empty: {
    title: "No decisions recorded yet",
    detail:
      "The operator has no rows in operator_decision_log for this range — a real zero, not a loading gap. Decisions are logged when an operator acts on a prediction or selects/creates a creative angle.",
  },
  error: {
    title: "Could not read operator analytics",
    detail: "The request failed. Nothing has been changed.",
  },
};

function resolveState(resource) {
  if (!resource) return "loading";
  if (resource.status === "loading") return "loading";
  if (resource.status === "disabled") return "disabled";
  if (resource.status === "error") {
    const code = resource.error && resource.error.response && resource.error.response.status;
    if (code === 403) return "denied";
    if (code === 404) return "notfound";
    return "error";
  }
  const total = resource.data && resource.data.decision_breakdown
    ? resource.data.decision_breakdown.total
    : 0;
  if (!total) return "empty";
  return "ok";
}

/** A figure, with the number leading and its label beneath.
 *
 * V2PORT-WS5: presentation only. The one semantic rule is that an absent or
 * zero figure is greyed and never coloured — this surface reports counts for an
 * operator, and a zero that reads as a healthy number would be a judgment the
 * endpoint deliberately refuses to make. Nothing here computes a score. */
function Kpi({ label, value, detail }) {
  const absent = value === "\u2014" || value === "-" || value == null;
  const zero = value === 0 || value === "0";
  return (
    <div className="oa-kpi" data-tone={absent ? "absent" : zero ? "zero" : "value"}>
      <div className="oa-kpi-v">{absent ? "\u2014" : value}</div>
      <div className="oa-kpi-k">{label}</div>
      {detail ? <div className="oa-kpi-d">{detail}</div> : null}
    </div>
  );
}

export default function OperatorAnalytics() {
  const { user } = useShell();
  const isFounder = user && user.role === "FOUNDER";
  const selfId = user && user.operator_id;

  // Founder gets a selector across the operator directory (`GET /operators`,
  // itself founder-only). A non-founder never issues this request — there is
  // nothing for them to pick, they can only ever see their own record.
  const directory = useBrainResource("/operators", { skip: !isFounder });
  const operators = useMemo(
    () => (Array.isArray(directory.data) ? directory.data : []),
    [directory.data]
  );

  const [selectedId, setSelectedId] = useState(selfId || "");
  useEffect(() => {
    if (!selectedId && selfId) setSelectedId(selfId);
  }, [selfId, selectedId]);

  const analytics = useBrainResource(
    selectedId ? `/operators/${encodeURIComponent(selectedId)}/analytics` : "",
    { skip: !selectedId }
  );
  const state = resolveState(analytics);
  const data = analytics.data || {};
  const breakdown = data.decision_breakdown || {};
  const outcome = data.outcome_resolution || {};
  const ttd = data.time_to_decision || {};
  const wfByStatus = data.workflow_executions_by_status || {};

  const selectedOperator = operators.find((o) => o.operator_id === selectedId);

  return (
    <>
      <PageHead
        title="Operator Analytics"
        subtitle="Objective decision counts for one operator — no scores, no speculative judgments."
        actions={
          isFounder && operators.length > 0 ? (
            <select
              className="oa-select"
              aria-label="Operator"
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
            >
              {selfId ? <option value={selfId}>Me{selfId ? ` (${selfId})` : ""}</option> : null}
              {operators
                .filter((o) => o.operator_id !== selfId)
                .map((o) => (
                  <option key={o.operator_id} value={o.operator_id}>
                    {o.name || o.email || o.operator_id}
                  </option>
                ))}
            </select>
          ) : null
        }
      />

      {!selectedId ? (
        <EmptyState
          title="No operator selected"
          detail="Signed-in operator id is not available from the session."
        />
      ) : state !== "ok" ? (
        <EmptyState title={STATES[state].title} detail={STATES[state].detail} />
      ) : (
        <div className="oa">
          <p className="oa-target dim">
            Showing analytics for{" "}
            <span className="mono">
              {(selectedOperator && (selectedOperator.name || selectedOperator.email)) || selectedId}
            </span>
            {data.date_from || data.date_to ? (
              <>
                {" "}· {data.date_from || "start"} to {data.date_to || "now"}
              </>
            ) : null}
          </p>

          <SectionTitle>Decisions</SectionTitle>
          <div className="oa-kpi-row">
            <Kpi label="Total decisions" value={breakdown.total ?? 0} />
            <Kpi label="Followed" value={breakdown.followed ?? 0} />
            <Kpi label="Overridden" value={breakdown.overridden ?? 0} />
            <Kpi label="Ignored" value={breakdown.ignored ?? 0} />
            <Kpi
              label="Uncategorized"
              value={breakdown.uncategorized ?? 0}
              detail="No FOLLOWED/OVERRIDDEN/IGNORED marker — e.g. angle-selection decisions"
            />
          </div>

          <SectionTitle>Outcome resolution</SectionTitle>
          <div className="oa-kpi-row">
            <Kpi label="Resolved" value={outcome.resolved ?? 0} />
            <Kpi label="Unresolved" value={outcome.unresolved ?? 0} />
            <Kpi
              label="Resolution rate"
              value={
                outcome.resolution_rate_pct === null || outcome.resolution_rate_pct === undefined
                  ? "—"
                  : `${outcome.resolution_rate_pct}%`
              }
            />
            <Kpi
              label="Correct / Incorrect / Inconclusive"
              value={`${(outcome.by_outcome && outcome.by_outcome.CORRECT) ?? 0} / ${
                (outcome.by_outcome && outcome.by_outcome.INCORRECT) ?? 0
              } / ${(outcome.by_outcome && outcome.by_outcome.INCONCLUSIVE) ?? 0}`}
            />
          </div>

          <SectionTitle>Time to decision</SectionTitle>
          <div className="oa-kpi-row">
            <Kpi
              label="Sample size"
              value={ttd.sample_size ?? 0}
              detail="Decisions linked to a prediction_id only"
            />
            <Kpi
              label="Avg"
              value={ttd.avg_seconds != null ? `${ttd.avg_seconds}s` : "—"}
            />
            <Kpi
              label="Min"
              value={ttd.min_seconds != null ? `${ttd.min_seconds}s` : "—"}
            />
            <Kpi
              label="Max"
              value={ttd.max_seconds != null ? `${ttd.max_seconds}s` : "—"}
            />
          </div>

          <SectionTitle>Decisions per week</SectionTitle>
          {(data.decisions_per_week || []).length === 0 ? (
            <p className="dim">No weekly rows in range.</p>
          ) : (
            <div className="table-wrap">
              <table className="oa-table">
                <thead>
                  <tr>
                    <th>Week</th>
                    <th>Total</th>
                    <th>Followed</th>
                    <th>Overridden</th>
                    <th>Ignored</th>
                    <th>Uncategorized</th>
                  </tr>
                </thead>
                <tbody>
                  {data.decisions_per_week.map((row) => (
                    <tr key={row.iso_week}>
                      <td className="mono">{row.iso_week}</td>
                      <td className="num">{row.total}</td>
                      <td className="num">{row.followed}</td>
                      <td className="num">{row.overridden}</td>
                      <td className="num">{row.ignored}</td>
                      <td className="num">{row.uncategorized}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <SectionTitle>Workflow executions by status (platform-wide)</SectionTitle>
          {Object.keys(wfByStatus).length === 0 ? (
            <p className="dim">No workflow_events rows in range.</p>
          ) : (
            <div className="table-wrap">
              <table className="oa-table">
                <thead>
                  <tr>
                    <th>Workflow</th>
                    {Array.from(
                      new Set(Object.values(wfByStatus).flatMap((s) => Object.keys(s)))
                    ).map((status) => (
                      <th key={status}>{status}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(wfByStatus).map(([wid, statuses]) => (
                    <tr key={wid}>
                      <td className="mono">{wid}</td>
                      {Array.from(
                        new Set(Object.values(wfByStatus).flatMap((s) => Object.keys(s)))
                      ).map((status) => (
                        <td key={status} className="num">
                          {statuses[status] ?? 0}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <p className="oa-note faint">
            workflow_events carries no operator dimension — this table is the
            platform-wide picture, shown as context, not attributed to this
            operator.
          </p>
        </div>
      )}
    </>
  );
}
